/**
 * MomentScience SDK public entry.
 *
 * Exposes a `MS` namespace supporting three usage styles:
 *   1. Direct factory:   const inst = MS.init(config, user);
 *   2. Command queue:    MS('init', 'name', config, user);   (async-script safe)
 *   3. Ready promise:    MS.ready.then(ms => ms.init(config));
 */
import { MomentScienceSDK, } from './SDK';
import { bootstrapLegacyAdpx } from './compat/legacyAdpx';
import { createDebugLogger } from './utils/debug';
const debug = createDebugLogger('index');
const instances = new Map();
function init(config, userOrName, name) {
    const resolvedName = (typeof userOrName === 'string' ? userOrName : name) ??
        config.name ??
        `ms-${instances.size + 1}`;
    const resolvedUser = typeof userOrName === 'object' ? userOrName : undefined;
    const existing = instances.get(resolvedName);
    if (existing) {
        debug.warn(`Instance "${resolvedName}" already exists. Returning existing instance.`);
        return existing;
    }
    const inst = new MomentScienceSDK(resolvedName);
    instances.set(resolvedName, inst);
    inst.init({ ...config, user: { ...(resolvedUser ?? {}), ...(config.user ?? {}) } });
    return inst;
}
function getInstance(name) {
    return instances.get(name);
}
/**
 * Command dispatcher for the queue-style API.
 * Supported shapes:
 *   ('init', name, config, user)   → creates and initializes an instance
 *   ('init', config, user)         → derives name from config.name or auto-assigns
 *   (methodName, name, ...args)    → invokes method on named instance
 */
function dispatch(cmd, ...rest) {
    if (typeof cmd !== 'string') {
        debug.warn('Unknown command (expected string):', cmd);
        return undefined;
    }
    if (cmd === 'init') {
        const [a, b, c] = rest;
        // ('init', name, config, user?)
        if (typeof a === 'string' && b && typeof b === 'object') {
            return init(b, c, a);
        }
        // ('init', config, user?)
        if (a && typeof a === 'object') {
            return init(a, b);
        }
        debug.warn('init: invalid arguments', rest);
        return undefined;
    }
    // Method dispatch: (method, name, ...args)
    const [name, ...methodArgs] = rest;
    if (typeof name !== 'string') {
        debug.warn(`Command "${cmd}" requires instance name as second argument.`);
        return undefined;
    }
    const inst = instances.get(name);
    if (!inst) {
        debug.warn(`Unknown instance "${name}" for command "${cmd}".`);
        return undefined;
    }
    const fn = inst[cmd];
    if (typeof fn !== 'function') {
        debug.warn(`Instance "${name}" has no method "${cmd}".`);
        return undefined;
    }
    return fn.apply(inst, methodArgs);
}
// Build the callable MS function (for command-queue invocations).
const MS = function (...args) {
    return dispatch(args[0], ...args.slice(1));
};
MS.init = init;
MS.getInstance = getInstance;
MS.instances = instances;
MS.MomentScienceSDK = MomentScienceSDK;
MS.ready = Promise.resolve(MS);
MS.version = __VERSION__;
// Drain any queue stub set up by publishers before this script finished loading.
// Supports both shapes publishers might use:
//   window.MS = window.MS || function(){(MS.q=MS.q||[]).push(arguments)}
//   window.MS.q = [];
if (typeof window !== 'undefined') {
    const existing = window.MS;
    const pending = [];
    if (existing && Array.isArray(existing.q)) {
        pending.push(...existing.q);
    }
    for (const entry of pending) {
        try {
            const asArray = Array.isArray(entry)
                ? entry
                : Array.prototype.slice.call(entry);
            dispatch(asArray[0], ...asArray.slice(1));
        }
        catch (e) {
            debug.warn('Error draining queued command:', entry, e);
        }
    }
    // Future calls use the MS function directly; keep .q as a live-push array
    // in case publishers hold a reference to it from the original stub.
    MS.q = {
        push: (args) => {
            const asArray = Array.isArray(args)
                ? args
                : Array.prototype.slice.call(args);
            dispatch(asArray[0], ...asArray.slice(1));
        },
    };
    bootstrapLegacyAdpx(MS);
}
// Named exports are not emitted under the IIFE bundle (only `default` becomes
// `window.MS`). Consumers of a future ESM/UMD build can import the class via
// `MS.MomentScienceSDK`.
export default MS;
